import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { CreditMemoLineItemFieldsFragment } from '@/types';
import { formatCurrency, getOrderItemPricesFieldsToUse } from '@/utils/money';
import { getPackDisplayString } from '@/utils/item';
import { ItemSubtitle } from '@/components/item-subtitle';
import { Props as ListItemProps, ListOption } from '@/components/list-item';
import { OrderItemListItem } from '@/components/order-item-list-item';
import { PackUnit } from '@/enums';
import { RecyclerList } from '@/components/recycler-list';
import { ScrollViewProps } from 'react-native';
import { usePaymentsContext } from '@/contexts/payments-context';

interface CreditMemoLineItemMetadata {
  itemCode: string | null;
  itemName: string;
  maxUnitSize: number;
  minUnitSize: number;
  modifierString: string | null;
  packUnit: PackUnit | null;
  quantity: number;
  unitCount: number;
  unitPriceInMicros: number;
}

const getCreditMemoLineItemMetadata = (
  option: CreditMemoLineItemFieldsFragment,
): CreditMemoLineItemMetadata => {
  if (option.orders_item) {
    const orderItem = option.orders_item;
    const itemPricesToUse = getOrderItemPricesFieldsToUse(orderItem);
    return {
      itemCode: orderItem.variants_pack.external_item_id ?? null,
      itemName: orderItem.restaurant_display_name,
      maxUnitSize: orderItem.pack.max_unit_size,
      minUnitSize: orderItem.pack.min_unit_size,
      modifierString: orderItem.orders_items_modifier_options
        .map((o) => o.name)
        .join(', '),
      packUnit: orderItem.pack.unit as PackUnit,
      quantity: itemPricesToUse?.quantity ?? 0,
      unitCount: orderItem.pack.unit_count,
      unitPriceInMicros:
        itemPricesToUse?.unitPriceInMicros ?? option.unit_price_in_micros,
    };
  } else if (option.variants_pack) {
    const variantPack = option.variants_pack;
    return {
      itemCode: variantPack.external_item_id ?? null,
      itemName: variantPack.item.name,
      maxUnitSize: variantPack.pack.max_unit_size,
      minUnitSize: variantPack.pack.min_unit_size,
      modifierString: null,
      packUnit: variantPack.pack.unit as PackUnit,
      quantity: option.quantity,
      unitCount: variantPack.pack.unit_count,
      unitPriceInMicros: option.unit_price_in_micros,
    };
  }

  return {
    itemCode: option.item_code ?? null,
    itemName: option.item_name ?? i18n.t('credited_item'),
    maxUnitSize: 1,
    minUnitSize: 1,
    modifierString: null,
    packUnit: option.pack_unit as PackUnit,
    quantity: option.quantity,
    unitCount: 1,
    unitPriceInMicros: option.unit_price_in_micros,
  };
};

type CreditMemoDetailItemOption = ListOption<CreditMemoLineItemMetadata>;

interface Props {
  creditMemoLineItems: CreditMemoLineItemFieldsFragment[];
  scrollViewProps?: ScrollViewProps;
}

export const CreditMemoDetailList: React.FC<Props> = ({
  creditMemoLineItems,
  scrollViewProps,
}) => {
  const { currencyCode } = usePaymentsContext();

  const sections = useMemo(() => {
    return [
      {
        data: creditMemoLineItems.map<CreditMemoDetailItemOption>((li) => {
          const metadata = getCreditMemoLineItemMetadata(li);
          return {
            metadata,
            title: metadata.itemName,
            value: metadata.itemName,
          };
        }),
        key: null,
      },
    ];
  }, [creditMemoLineItems]);

  const listItemProps = useMemo<
    Partial<ListItemProps<CreditMemoDetailItemOption>>
  >(() => {
    return {
      renderMainContent: (option) => {
        const metadata = option.metadata!;
        return (
          <OrderItemListItem
            description={null}
            formattedPrice={formatCurrency(
              -metadata.unitPriceInMicros * metadata.quantity,
              currencyCode,
            )}
            quantity={metadata.quantity}
            subtitle={
              <ItemSubtitle
                currencyCode={currencyCode}
                displayString={getPackDisplayString({
                  maxUnitSize: metadata.maxUnitSize,
                  minUnitSize: metadata.minUnitSize,
                  unit: metadata.packUnit,
                })}
                modifierString={metadata.modifierString ?? undefined}
                showInlineModifiers={Boolean(metadata.packUnit)}
                sku={metadata.itemCode}
                unitCount={metadata.unitCount}
                unitPriceInMicros={metadata.unitPriceInMicros}
              />
            }
            title={option.title}
          />
        );
      },
    };
  }, [currencyCode]);

  return (
    <RecyclerList
      estimatedItemSize={[140, 120, 120, 90]}
      listItemProps={listItemProps}
      sections={sections}
      testID="credit-memo-detail-list"
      {...scrollViewProps}
    />
  );
};
